/**
 * PreviewControls: the Today / Proposed, State and Choice switches above a
 * preview. They live in the app, not in the drafted document, so they look
 * and work the same on every repository; the document only marks its parts
 * (`data-yagni-*`), and the frame's bridge shows the picked ones.
 */
import type { PrototypePreviewParts, PrototypePreviewView } from "@yagni/shared";
import { DESIGN_COPY } from "../work/designCopy.js";
import "../work/caseFile.css";

/** The view a preview opens on: Proposed, its first state, each choice's first option. */
export function defaultPreviewView(
  parts: PrototypePreviewParts | null | undefined,
): PrototypePreviewView {
  const choices: Record<string, string> = {};
  for (const choice of parts?.choices ?? []) {
    const first = choice.options[0];
    if (first) choices[choice.key] = first.key;
  }
  return {
    view: parts?.views.includes("proposed") ? "proposed" : (parts?.views[0] ?? null),
    state: parts?.states[0]?.key ?? null,
    choices,
  };
}

/** Whether a preview has anything to switch: both views, two states, or a choice. */
export function hasPreviewControls(parts: PrototypePreviewParts | null | undefined): boolean {
  if (!parts) return false;
  return (
    (parts.views.includes("today") && parts.views.includes("proposed")) ||
    parts.states.length > 1 ||
    parts.choices.some((choice) => choice.options.length > 1)
  );
}

function Seg({
  label,
  options,
  value,
  onChange,
  accent,
}: {
  label: string;
  options: ReadonlyArray<{ key: string; label: string }>;
  value: string | null;
  onChange: (key: string) => void;
  accent?: boolean;
}) {
  return (
    <span className={accent ? "seg lvl" : "seg"} role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          role="radio"
          aria-checked={option.key === value}
          className={option.key === value ? "on" : undefined}
          onClick={() => onChange(option.key)}
        >
          {option.label}
        </button>
      ))}
    </span>
  );
}

export function PreviewControls({
  parts,
  value,
  onChange,
}: {
  parts: PrototypePreviewParts | null | undefined;
  value: PrototypePreviewView;
  onChange: (next: PrototypePreviewView) => void;
}) {
  if (!parts || !hasPreviewControls(parts)) return null;
  const bothViews = parts.views.includes("today") && parts.views.includes("proposed");
  return (
    <div className="cf-preview-ctl" data-testid="preview-controls">
      {bothViews && (
        <Seg
          label="Preview view"
          accent
          options={[
            { key: "today", label: DESIGN_COPY.today },
            { key: "proposed", label: DESIGN_COPY.proposed },
          ]}
          value={value.view}
          onChange={(key) => onChange({ ...value, view: key === "today" ? "today" : "proposed" })}
        />
      )}
      {parts.states.length > 1 && (
        <>
          <span className="cf-preview-ctl-lab">{DESIGN_COPY.state}</span>
          <Seg
            label={DESIGN_COPY.state}
            options={parts.states}
            value={value.state}
            onChange={(key) => onChange({ ...value, state: key })}
          />
        </>
      )}
      {parts.choices
        .filter((choice) => choice.options.length > 1)
        .map((choice) => (
          <span key={choice.key} className="cf-preview-ctl-choice">
            <span className="cf-preview-ctl-lab">{DESIGN_COPY.choice}</span>
            <Seg
              label={choice.label}
              options={choice.options}
              value={value.choices[choice.key] ?? null}
              onChange={(key) =>
                onChange({ ...value, choices: { ...value.choices, [choice.key]: key } })
              }
            />
            <span className="cf-preview-ctl-q">{choice.label}</span>
          </span>
        ))}
    </div>
  );
}
